/**
 * TeamAnswersDialog — ★ Fase 59: l'admin legge le risposte libere di un membro.
 *
 * Carica GET /api/training/admin/answers/{user_id} e mostra le coppie
 * domanda/risposta della missione «Raccontaci come vendi».
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { api } from '../../lib/api';
import { Skeleton } from '../ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { MessageSquareText } from 'lucide-react';

const dateLocale = () => {
  const lng = i18n.language || 'it';
  if (lng.startsWith('es')) return 'es-ES';
  if (lng.startsWith('en')) return 'en-GB';
  return 'it-IT';
};

export function TeamAnswersDialog({ row, open, onClose }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !row) return;
    setData(null);
    setError(null);
    api.get(`/training/admin/answers/${row.user_id}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || t('trainingPage.answersLoadError', 'Errore caricamento risposte')));
  }, [open, row, t]);

  if (!row) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="team-answers-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquareText className="w-5 h-5 text-[hsl(var(--primary))]" />
            {t('trainingPage.answersOf', 'Risposte di')} {row.name}
          </DialogTitle>
          <DialogDescription>{row.email}</DialogDescription>
        </DialogHeader>

        {error && (
          <div className="text-sm text-red-600 py-4" data-testid="team-answers-error">{error}</div>
        )}

        {!data && !error && (
          <div className="space-y-3 py-2">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16" />)}
          </div>
        )}

        {data && (data.items || []).length === 0 && (
          <div className="text-sm text-muted-foreground py-4" data-testid="team-answers-empty">
            {t('trainingPage.noAnswersYet', 'Nessuna risposta libera ancora inviata.')}
          </div>
        )}

        {data && (data.items || []).map((item) => (
          <div key={item.mission_key} className="space-y-3" data-testid={`team-answers-item-${item.mission_key}`}>
            <div className="text-xs text-muted-foreground">
              {item.title}
              {item.submitted_at && (
                <> · {t('trainingPage.sentOn', 'inviate il')} {new Date(item.submitted_at).toLocaleDateString(dateLocale())}</>
              )}
            </div>
            {(item.qa || []).map((pair, i) => (
              <div key={pair.q} className="rounded-lg border border-border bg-[var(--surface-2,#F7F5F0)] p-3 space-y-1">
                <div className="text-xs font-semibold text-[hsl(var(--primary))]">
                  {i + 1}. {pair.q}
                </div>
                <p className="text-sm whitespace-pre-wrap" data-testid={`team-answer-text-${i}`}>
                  {pair.a || '—'}
                </p>
              </div>
            ))}
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}
